import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const fmtEur = (n) => `€${(Number(n || 0) / 1e6).toFixed(0)}M`;

/**
 * LoanOptionDecisions — banner "Diritto di riscatto" da decidere.
 * Mostra i giocatori in prestito con diritto di riscatto in scadenza: il presidente
 * sceglie ESERCITA (paga il riscatto, lo tiene) oppure RIFIUTA (torna all'origine).
 * Admin vede tutte le decisioni in sospeso; il presidente solo quelle della sua squadra.
 */
export default function LoanOptionDecisions({ onResolved }) {
    const [decisions, setDecisions] = useState([]);
    const [busyId, setBusyId] = useState(null);
    const [err, setErr] = useState("");

    const load = useCallback(() => {
        api.get("/loans/option-decisions")
            .then(({ data }) => setDecisions(data || []))
            .catch(() => setDecisions([]));
    }, []);

    useEffect(() => { load(); }, [load]);

    const act = async (dec, action) => {
        const verb = action === "exercise" ? "ESERCITARE" : "RIFIUTARE";
        const detail = action === "exercise"
            ? `Pagherai ${fmtEur(dec.buyback_amount)} a ${dec.origin_team_name} e ${dec.player_name} diventa tuo a titolo definitivo.`
            : `${dec.player_name} tornerà a ${dec.origin_team_name}. Nessun pagamento.`;
        if (!window.confirm(`Vuoi ${verb} il diritto di riscatto di ${dec.player_name}?\n\n${detail}`)) return;
        setBusyId(dec.id);
        setErr("");
        try {
            const r = await api.post(`/loans/option-decisions/${dec.id}/${action}`);
            if (action === "exercise") {
                window.alert(`✓ ${r.data.player_name} riscattato per ${fmtEur(r.data.amount)} — nuovo stipendio ${fmtEur(r.data.new_wage)}.`);
            } else {
                window.alert(`✓ ${r.data.player_name} è tornato a ${r.data.origin_team_name}.`);
            }
            load();
            if (onResolved) onResolved();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusyId(null);
        }
    };

    if (decisions.length === 0) return null;

    return (
        <div className="op-card mb-6 overflow-hidden border-l-2 border-l-t2bb6ff" data-testid="loan-option-decisions">
            <div className="px-5 py-4 border-b border-t22252a bg-t0e1014">
                <div className="op-label text-t2bb6ff">⚖️ DIRITTO DI RISCATTO — DA DECIDERE</div>
                <div className="op-mono text-[10px] text-t8f95a3 mt-1">
                    {decisions.length} giocatore/i in prestito con diritto di riscatto a fine stagione.
                </div>
            </div>
            {err && (
                <div className="px-5 py-2 bg-tff3b30/10 text-tff3b30 text-xs op-mono" data-testid="loan-option-error">{err}</div>
            )}
            <div className="divide-y divide-t181b20">
                {decisions.map((d) => (
                    <div key={d.id} className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`loan-option-row-${d.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="text-tffffff font-bold text-sm truncate">{d.player_name}</div>
                            <div className="op-mono text-[11px] text-t8f95a3">
                                {d.borrower_team_name} ← in prestito da {d.origin_team_name}
                                {" · "}riscatto <span className="text-t2bb6ff">{fmtEur(d.buyback_amount)}</span>
                            </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <button
                                onClick={() => act(d, "exercise")}
                                disabled={busyId === d.id}
                                className="op-btn text-xs disabled:opacity-50"
                                style={{ background: "rgb(var(--tc-2bb6ff))", color: "rgb(var(--tc-000000))", fontWeight: 700 }}
                                data-testid={`loan-option-exercise-${d.id}`}
                            >
                                ✓ Esercita ({fmtEur(d.buyback_amount)})
                            </button>
                            <button
                                onClick={() => act(d, "refuse")}
                                disabled={busyId === d.id}
                                className="op-btn text-xs disabled:opacity-50"
                                data-testid={`loan-option-refuse-${d.id}`}
                            >
                                ✕ Rifiuta
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
